import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {UserId} from "../../models/users/AuthenticatedUser";
import {DateRange} from "../../models/common/DateRange";
import {TimesheetEmployeeHoursReport} from "../../models/reporting/TimesheetEmployeeHoursReport";
import {AppState} from "../../reducers/RootReducer";
import {  StateObservable, ofType } from "redux-observable";
import { switchMap, map, catchError } from "rxjs/operators";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {currentLanguageFromStore} from "../../config/LanguageConfig";
import {eventNotificationShow} from "../common/EventNotificationActions";
import {EVENT_NOTIFICATION_TYPE} from "../../helpers/EventNotificationType";
import {createErrorResponse} from "../../config/languages/definitions/AjaxResponses";
import { ajax } from "rxjs/ajax";
import { Observable, of } from 'rxjs';
import {UserViewSettings, UserViewSettingsProp } from "../../models/common/ViewSettings";
import {getUrlParamsFromViewSettings} from "../../config/RouteConfig";
import {TimesheetModule} from "../../models/timesheet/Timesheet";
import {ApiReturnType} from "../../models/common/ApiReturnType";
import {Nullable} from "../../models/common/Nullable";
import {base64ToArrayBuffer, downloadFileHelper} from "../../helpers/FileHelpers";
import {defaultFileData, FileData} from "../../models/common/FileData";
import {ExportTypes} from "../../models/common/Export";

export type TimesheetReportActionTypes = FetchMenuTimesheetHoursReportActionTypes | FetchTimesheetHoursReportActionTypes

export interface FetchMenuTimesheetHoursReportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_MENU_TIMESHEET_HOURS_REPORT > {
    userIds: UserId[]
    dateRange: DateRange
}
export interface FetchMenuTimesheetHoursReportSuccessAction extends ReduxAction<typeof FETCH_MENU_TIMESHEET_HOURS_REPORT_SUCCESS> {
    reports: ApiReturnType< TimesheetEmployeeHoursReport[] >
}
export interface FetchMenuTimesheetHoursReportErrorAction extends ErrorAction<typeof FETCH_MENU_TIMESHEET_HOURS_REPORT_ERROR> {
    userIds: UserId[]
    dateRange: DateRange
}

export type FetchMenuTimesheetHoursReportActionTypes = FetchMenuTimesheetHoursReportAction
    | FetchMenuTimesheetHoursReportSuccessAction
    | FetchMenuTimesheetHoursReportErrorAction


export const FETCH_MENU_TIMESHEET_HOURS_REPORT = "FETCH_MENU_TIMESHEET_HOURS_REPORT";
export const FETCH_MENU_TIMESHEET_HOURS_REPORT_SUCCESS = "FETCH_MENU_TIMESHEET_HOURS_REPORT_SUCCESS";
export const FETCH_MENU_TIMESHEET_HOURS_REPORT_ERROR = "FETCH_MENU_TIMESHEET_HOURS_REPORT_ERROR";

export function fetchMenuTimesheetHoursReport(
    userIds: UserId[],
    dateRange: DateRange,
    userViewSettings: UserViewSettings
): FetchMenuTimesheetHoursReportAction {
    return { type: FETCH_MENU_TIMESHEET_HOURS_REPORT, userIds, dateRange, userViewSettings };
}
export function fetchMenuTimesheetHoursReportSuccess(
    reports: ApiReturnType< TimesheetEmployeeHoursReport[] >
): FetchMenuTimesheetHoursReportSuccessAction {
    return { type: FETCH_MENU_TIMESHEET_HOURS_REPORT_SUCCESS, reports };
}
export function fetchMenuTimesheetHoursReportError(
    error: Error,
    userIds: UserId[],
    dateRange: DateRange
): FetchMenuTimesheetHoursReportErrorAction {
    return {type: FETCH_MENU_TIMESHEET_HOURS_REPORT_ERROR, error, userIds, dateRange};
}

export function fetchMenuTimesheetHoursReportEpic (
    action$: Observable< FetchMenuTimesheetHoursReportAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_MENU_TIMESHEET_HOURS_REPORT ),
        switchMap( action => {
            const ids = action.userIds.map( uid => uid.rawValue ).join(",");
            const settingPath = getUrlParamsFromViewSettings( action.userViewSettings );
            const path = `${ APP_CONFIG.baseWeb }reporting/timesheet/employees/hours${ settingPath }&employeeIds=${ids}&start=${action.dateRange.start.getTime()}&end=${action.dateRange.endInclusive.getTime()}`;
            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchMenuTimesheetHoursReportSuccess( response.response as ApiReturnType< TimesheetEmployeeHoursReport[] > ) ),
                catchError( error => of(
                    fetchMenuTimesheetHoursReportError(error, action.userIds, action.dateRange ),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse(
                            language,
                            error.status,
                            TimesheetModule.identifier,
                            "fetchMenuTimesheetHoursReport"
                        )
                    )
                ) )
            )
        })
    );
}

export interface FetchTimesheetHoursReportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_TIMESHEET_HOURS_REPORT > {
    userIds: Nullable< UserId[] >
    dateRange: DateRange
}
export interface FetchTimesheetHoursReportSuccessAction extends ReduxAction<typeof FETCH_TIMESHEET_HOURS_REPORT_SUCCESS> {
    reports: ApiReturnType< TimesheetEmployeeHoursReport[]>
}
export interface FetchTimesheetHoursReportErrorAction extends ErrorAction<typeof FETCH_TIMESHEET_HOURS_REPORT_ERROR> {
}

export type FetchTimesheetHoursReportActionTypes = FetchTimesheetHoursReportAction
    | FetchTimesheetHoursReportSuccessAction
    | FetchTimesheetHoursReportErrorAction

export const FETCH_TIMESHEET_HOURS_REPORT = "FETCH_TIMESHEET_HOURS_REPORT";
export const FETCH_TIMESHEET_HOURS_REPORT_SUCCESS = "FETCH_TIMESHEET_HOURS_REPORT_SUCCESS";
export const FETCH_TIMESHEET_HOURS_REPORT_ERROR = "FETCH_TIMESHEET_HOURS_REPORT_ERROR";

export function fetchTimesheetHoursReport(
    userIds: Nullable< UserId[] >,
    dateRange: DateRange,
    userViewSettings: UserViewSettings
): FetchTimesheetHoursReportAction {
    return { type: FETCH_TIMESHEET_HOURS_REPORT, userIds, dateRange, userViewSettings };
}
export function fetchTimesheetHoursReportSuccess(
    reports: ApiReturnType< TimesheetEmployeeHoursReport[] >
): FetchTimesheetHoursReportSuccessAction {
    return { type: FETCH_TIMESHEET_HOURS_REPORT_SUCCESS, reports };
}
export function fetchTimesheetHoursReportError(
    error: Error
): FetchTimesheetHoursReportErrorAction {
    return {type: FETCH_TIMESHEET_HOURS_REPORT_ERROR, error };
}

export function fetchTimesheetHoursReportEpic (
    action$: Observable< FetchTimesheetHoursReportAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_TIMESHEET_HOURS_REPORT ),
        switchMap( action => {
            const idUrlParams = action.userIds?.reduce( ( acc, uid ) => acc+"&employeeIds="+uid.rawValue, "" ) ?? null;
            const settingPath = getUrlParamsFromViewSettings( action.userViewSettings );
            const path = `${ APP_CONFIG.baseWeb }reporting/timesheet/employees/hours${ settingPath }${ idUrlParams !== null ? idUrlParams : "" }&start=${action.dateRange.start.getTime()}&end=${action.dateRange.endInclusive.getTime()}`;
            const language = currentLanguageFromStore( store$ );

            return ajax( APP_CONFIG.getRequestObject( path ) ).pipe(
                map( response => fetchTimesheetHoursReportSuccess( response.response as ApiReturnType< TimesheetEmployeeHoursReport[] > ) ),
                catchError( error => of(
                    fetchTimesheetHoursReportError(error),
                    eventNotificationShow(
                        EVENT_NOTIFICATION_TYPE.customAjaxError,
                        createErrorResponse(
                            language,
                            error.status,
                            "",
                            "fetchTimesheetHoursReport"
                        )
                    )
                ) )
            )
        })
    );
}

/**
 * export
 */
export interface FetchTimesheetHoursReportExportAction extends UserViewSettingsProp, ReduxAction<typeof FETCH_TIMESHEET_HOURS_REPORT_EXPORT > {
    userIds: Nullable< UserId[] >
    dateRange: DateRange
    fileData: FileData
    exportTypes: ExportTypes
}
export interface FetchTimesheetHoursReportExportSuccessAction extends ReduxAction<typeof FETCH_TIMESHEET_HOURS_REPORT_EXPORT_SUCCESS> {
}
export interface FetchTimesheetHoursReportExportErrorAction extends ErrorAction<typeof FETCH_TIMESHEET_HOURS_REPORT_EXPORT_ERROR> {
}

export type FetchTimesheetHoursReportExportActionTypes = FetchTimesheetHoursReportExportAction
    | FetchTimesheetHoursReportExportSuccessAction
    | FetchTimesheetHoursReportExportErrorAction

export const FETCH_TIMESHEET_HOURS_REPORT_EXPORT = "FETCH_TIMESHEET_HOURS_REPORT_EXPORT";
export const FETCH_TIMESHEET_HOURS_REPORT_EXPORT_SUCCESS = "FETCH_TIMESHEET_HOURS_REPORT_EXPORT_SUCCESS";
export const FETCH_TIMESHEET_HOURS_REPORT_EXPORT_ERROR = "FETCH_TIMESHEET_HOURS_REPORT_EXPORT_ERROR";

export function fetchTimesheetHoursReportExport(
    exportTypes: ExportTypes,
    userIds: Nullable< UserId[] >,
    dateRange: DateRange,
    userViewSettings: UserViewSettings,
    fileData: FileData
): FetchTimesheetHoursReportExportAction {
    return {
        type: FETCH_TIMESHEET_HOURS_REPORT_EXPORT,
        userIds,
        dateRange,
        userViewSettings ,
        exportTypes,
        fileData };
}
export function fetchTimesheetHoursReportExportSuccess(): FetchTimesheetHoursReportExportSuccessAction {
    return { type: FETCH_TIMESHEET_HOURS_REPORT_EXPORT_SUCCESS };
}
export function fetchTimesheetHoursReportExportError(
    error: Error
): FetchTimesheetHoursReportExportErrorAction {
    return {type: FETCH_TIMESHEET_HOURS_REPORT_EXPORT_ERROR, error };
}

export function fetchTimesheetHoursReportExportEpic (
    action$: Observable< FetchTimesheetHoursReportExportAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( FETCH_TIMESHEET_HOURS_REPORT_EXPORT ),
        switchMap( action => {
            const idUrlParams = action.userIds?.reduce( ( acc, uid ) => acc+"&employeeIds="+uid.rawValue, "" ) ?? null;
            const settingPath = getUrlParamsFromViewSettings( action.userViewSettings );
            const path = `${ APP_CONFIG.baseWeb }reporting/timesheet/employees/hours/export/${ action.exportTypes }${ settingPath }${ idUrlParams !== null ? idUrlParams : "" }&start=${action.dateRange.start.getTime()}&end=${action.dateRange.endInclusive.getTime()}`;
            const language = currentLanguageFromStore( store$ );

            return ajax({
                ...APP_CONFIG.getRequestObject(path),
                responseType: "text",
                async: false
            }).pipe(
                map( response => {

                    //@ts-ignore
                    downloadFileHelper( response.response, action.fileData );

                    return fetchTimesheetHoursReportExportSuccess();
                } ),
                catchError( error => {
                    return of(
                        fetchTimesheetHoursReportExportError(error),
                        eventNotificationShow(
                            EVENT_NOTIFICATION_TYPE.customAjaxError,
                            createErrorResponse(
                                language,
                                error.status ?? 200,
                                "",
                                "fetchTimesheetHoursReportExport"
                            )
                        )
                    )
                } )
            )
        })
    );
}